import * as React from 'react';
import { cn } from '@/lib/utils';

const MAIN_CONTENT_ID = 'main-content';

/**
 * SkipLink Component
 * A visually hidden link that becomes visible only when focused via keyboard (e.g. Tab).
 * Uses focus-visible so it stays hidden when programmatically focused after mouse clicks.
 * Allows keyboard users to skip navigation and jump directly to main content.
 * WCAG 2.2 AA requirement for accessibility.
 * Matches guarantor portal implementation.
 * Focuses main content without scrolling the page (keeps view at top).
 */
export const SkipLink: React.FC<{
  href?: string;
  children?: React.ReactNode;
  className?: string;
}> = ({
  href = '#main-content',
  children = 'Skip to main content',
  className,
}) => {
  const skipToMain = () => {
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (main) {
      main.focus({ preventScroll: true });
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  return (
    <a
      href={href}
      data-skip-link
      onClick={(e) => {
        e.preventDefault();
        skipToMain();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          skipToMain();
        }
      }}
      className={cn(
        'sr-only',
        'focus-visible:not-sr-only focus-visible:fixed focus-visible:top-4 focus-visible:left-4 focus-visible:z-[9999]',
        'focus-visible:px-4 focus-visible:py-2 focus-visible:bg-primary focus-visible:text-primary-foreground',
        'focus-visible:rounded-md focus-visible:outline-none focus-visible:shadow-primary-focus',
        'transition-all duration-200',
        className
      )}
    >
      {children}
    </a>
  );
};
